import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { VyTable } from '../../../../../../../components/new-design/table/vy-table/VyTable';
import { useUpdateUserName } from '../../../../../../../hooks/requests/use-update-user-name';
import { useUpdateUserRole } from '../../../../../../../hooks/requests/use-update-user-role';
import { useUsers } from '../../../../../../../hooks/requests/use-users';
import { runMutation } from '../../../../../../../utils/vy-mutation';
import { getMembersColumns } from './members-table-components/MembersColumns';
import type { RoleLabelValuePair } from './members-table-types/role-label-value-pair';

export const MembersTable: React.FC = () => {
  const { data } = useUsers({ suspense: true });
  const users = data?.users ?? [];
  const updateUserRoleMutation = useUpdateUserRole();
  const updateUserNameMutation = useUpdateUserName();

  const onChangeUserRole = (userId: string) => {
    return (labelValuePair: RoleLabelValuePair) =>
      runMutation(
        updateUserRoleMutation,
        {
          userId,
          role: labelValuePair.role,
        },
        {
          pending: `Updating user role to ${labelValuePair.label}...`,
          success: `User role updated to ${labelValuePair.label}`,
          error: `Failed to update user role to ${labelValuePair.label}`,
        },
      );
  };

  const onChangeUserName = (userId: string, field: 'firstName' | 'lastName', value: string) =>
    runMutation(
      updateUserNameMutation,
      { userId, [field]: value },
      {
        pending: 'Updating member name...',
        success: 'Member name updated',
        error: 'Failed to update member name',
      },
    );

  const membersColumns = getMembersColumns({ onChangeUserRole, onChangeUserName });

  return (
    <Stack
      direction="row"
      sx={{
        gap: 4,
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "wrap",
        paddingX: "20px"
      }}>
      <Box>
        <Typography>Team members</Typography>

        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          The members in your organization
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          maxWidth: 1000,
          width: '100%',
          overflowX: 'auto',
        }}
      >
        <VyTable
          tableName="settings-members"
          itemsCount={users.length}
          showPagination={false}
          disableColumnResize
          items={users}
          columns={membersColumns}
        />
      </Box>
    </Stack>
  );
};
